import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { getObjectId, type Client, type Order } from '@hero/schema';
import { useOrderUpdateApi } from '@hero/api-hooks';
import { getClientIdFromOrder } from '@hero/web-hooks';
import { Modal } from '../../../app/Modal';
import { Button } from '../../../ui/Button';
import { ClientSelector } from '../../Client/Selector/ClientSelector';

export interface OrderChangeClientModalProps {
  order: Order;
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called after a successful assign/change (modal already closed).
   * Used by the price flow: pick a client first, then open the
   * price drawer once `Order.client` + `contracts[0]` exist.
   */
  onAssigned?: () => void;
  /**
   * When `before_price`, copy explains that a client is required
   * before a price can be set.
   */
  purpose?: 'default' | 'before_price';
}

/**
 * Assign or change `Order.client` via `OrderUpdateAPI.clientId`.
 * Reuses the shared {@link ClientSelector} (pick + create).
 */
export const OrderChangeClientModal: React.FC<OrderChangeClientModalProps> = ({
  order,
  isOpen,
  onClose,
  onAssigned,
  purpose = 'default',
}) => {
  const { t } = useTranslation(['m-order', 'c-main']);
  const existingClientId = useMemo(() => getClientIdFromOrder(order), [order]);
  const [clientId, setClientId] = useState<string>('');

  useEffect(() => {
    if (!isOpen) return;
    setClientId(existingClientId ?? '');
  }, [isOpen, existingClientId]);

  const updateOrder = useOrderUpdateApi({
    onSuccess: () => {
      toast.success(t('m-order:toast.success.order_client_updated'));
      onClose();
      onAssigned?.();
    },
    onError: (error: Error) => {
      toast.error(t('m-order:toast.error.order_client_update', { message: error.message }));
    },
  });

  const handleClientsSelected = (clients: Client[] | undefined) => {
    const next = clients?.[0];
    setClientId(next?._id ? String(getObjectId(next._id)) : '');
  };

  const handleConfirm = () => {
    if (!clientId || updateOrder.isPending) return;
    updateOrder.mutate({
      _id: getObjectId(order._id),
      clientId: getObjectId(clientId),
    });
  };

  const handleClose = () => {
    if (updateOrder.isPending) return;
    onClose();
  };

  const isAssign = !existingClientId;
  const canConfirm = !!clientId && !updateOrder.isPending;
  const descriptionKey =
    purpose === 'before_price'
      ? 'm-order:modal.change_client.before_price_description'
      : 'm-order:modal.change_client.description';

  return (
    <Modal
      btnTitle=""
      isOpen={isOpen}
      onClose={handleClose}
      modalTitle={
        isAssign
          ? t('m-order:modal.change_client.assign_title')
          : t('m-order:modal.change_client.change_title')
      }>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{t(descriptionKey)}</p>

        <ClientSelector
          selectedClients={clientId || undefined}
          onClientsSelected={handleClientsSelected}
          onClientSave={({ client }) => {
            setClientId(String(getObjectId(client._id)));
          }}
          label={t('m-order:modal.change_client.client_label')}
          placeholder={t('m-order:modal.change_client.client_placeholder')}
          disabled={updateOrder.isPending}
        />

        <div className="flex gap-2 pt-2 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={updateOrder.isPending}>
            {t('c-main:crud_buttons.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={!canConfirm}>
            {updateOrder.isPending
              ? t('m-order:modal.change_client.saving')
              : purpose === 'before_price'
                ? t('m-order:modal.change_client.confirm_and_set_price')
                : t('m-order:modal.change_client.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
